import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Link,
  InputAdornment,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grow,
  Fade,
  Slide,
  Zoom,
  Grid,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import api, { membershipApi } from '../../services/api'
import SignupHeader from './SignupHeader'
import { AddressAutocomplete } from '@/components/AddressAutocomplete'
import LocationSelector from '@/components/ui/LocationSelector'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import dayjs from 'dayjs'
import { calculatePasswordStrength, PasswordStrength } from '@/utils/validation'
import PasswordStrengthMeter from '@/components/ui/PasswordStrengthMeter'
import PhoneInput from '@/components/ui/PhoneInput'
import { getUSPhoneErrorMessage, validateUSPhone } from '@/utils/phoneUtils'
import ReCAPTCHA from 'react-google-recaptcha'
import { use } from 'marked'

interface PatientFormData {
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
  phone: string
  gender: string
  dateOfBirth: Date | null
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  country: string
  postalCode: string
  latitude?: number
  longitude?: number
  careNeeds: string[]
  therapyHistory: string[]
  sessionFrequency: string
  careGoal: string
}

interface CareNeed {
  _id: string
  name: string
}

interface TherapyHistory {
  id: string
  name: string
}

const PatientCreateAccountPage: React.FC = () => {
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>(
    calculatePasswordStrength('')
  )
  const [formData, setFormData] = useState<PatientFormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    gender: '',
    dateOfBirth: null,
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    latitude: undefined,
    longitude: undefined,
    careNeeds: [],
    therapyHistory: [],
    sessionFrequency: '',
    careGoal: '',
  })

  const [currentStep, setCurrentStep] = useState(0)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [showSuccessAlert, setShowSuccessAlert] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [phoneTouched, setPhoneTouched] = useState(false)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const [recaptchaError, setRecaptchaError] = useState('')
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY
  const recaptchaRef = useRef<ReCAPTCHA | null>(null)
  const isRecaptchaEnabled = Boolean(recaptchaSiteKey)

  // Dynamic dropdown data
  const [careNeedsOptions, setCareNeedsOptions] = useState<CareNeed[]>([])
  const [therapyHistoryOptions, setTherapyHistoryOptions] = useState<TherapyHistory[]>(
    []
  )
  const [loadingCareNeeds, setLoadingCareNeeds] = useState(false)

  // Static gender options
  const genderOptions = ['Male', 'Female', 'Other', 'Prefer not to say']
  const sessionFrequencyOptions = ['weekly', 'bi-weekly', 'as-needed']
  const [loadingTherapyHistory, setLoadingTherapyHistory] = useState(false)
  const steps = [
    'Personal Information',
    'Care Needs & History',
    'Session Preferences',
    'Care Goals',
  ]

  useEffect(() => {
    fetchCareNeeds()
  }, [])

  useEffect(() => {
    fetchTherapyHistoryOptions()
  }, [])

  const fetchCareNeeds = async () => {
    try {
      setLoadingCareNeeds(true)
      const response = await api.get('specilities/specility', {
        headers: {
          'Content-Type': 'application/json',
          Authorization:
            'Bearer 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',
        },
      })
      setCareNeedsOptions(response.data)
    } catch (error) {
      console.error('Error fetching care needs:', error)
      setError('Failed to load care needs. Please try again.')
    } finally {
      setLoadingCareNeeds(false)
    }
  }
  const fetchTherapyHistoryOptions = async () => {
    try {
      setLoadingTherapyHistory(true)
      const response = await api.get('/specilities/specility-intrust', {
        headers: {
          'Content-Type': 'application/json',
          Authorization:
            'Bearer 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',
        },
      })
      setTherapyHistoryOptions(response.data)
    } catch (error) {
      console.error('Error fetching therapy history options:', error)
      setError('Failed to load therapy history options. Please try again.')
    } finally {
      setLoadingTherapyHistory(false)
    }
  }

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validatePhone = (phone: string): boolean => {
    return validateUSPhone(phone)
  }

  const validatePassword = (password: string): boolean => {
    return password.length >= 6
  }

  const validatePostalCode = (postalCode: string): boolean => {
    return postalCode.length >= 3
  }

  const validateBasicInfo = (): boolean => {
    const errors: Record<string, string> = {}

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required'
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required'
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required'
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address'
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required'
    } else {
      const phoneError = getUSPhoneErrorMessage(formData.phone)
      if (phoneError) {
        errors.phone = phoneError
      }
    }

    if (!formData.password) {
      errors.password = 'Password is required'
    } else if (!validatePassword(formData.password)) {
      errors.password = 'Password must be at least 6 characters long'
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match'
    }

    if (!formData.gender || !formData.gender.trim()) {
      errors.gender = 'Gender is required'
    }

    if (!formData.dateOfBirth) {
      errors.dateOfBirth = 'Date of birth is required'
    }

    if (!formData.addressLine1.trim()) {
      errors.addressLine1 = 'Address line 1 is required'
    }

    if (!formData.city.trim()) {
      errors.city = 'City is required'
    }

    if (!formData.country.trim()) {
      errors.country = 'Country is required'
    }

    if (!formData.state.trim()) {
      errors.state = 'State is required'
    }

    if (!formData.postalCode.trim()) {
      errors.postalCode = 'Postal code is required'
    } else if (!validatePostalCode(formData.postalCode)) {
      errors.postalCode = 'Please enter a valid postal code'
    }

    // if (!formData.country.trim()) {
    //   errors.country = 'Country is required'
    // }

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const validateOnboardingStep = (step: number): boolean => {
    const errors: Record<string, string> = {}

    // Only validate step 0 (care needs) - it's mandatory
    if (step === 0) {
      if (formData.careNeeds.length === 0) {
        errors.careNeeds = 'Please select at least one care need'
      }
    }
    // Steps 1, 2, and 3 are optional - no validation needed

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleInputChange =
    (field: keyof PatientFormData) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event.target.value
        setFormData(prev => ({
          ...prev,
          [field]: value,
        }))
        // Clear field error when user starts typing
        if (fieldErrors[field]) {
          setFieldErrors(prev => ({
            ...prev,
            [field]: '',
          }))
        }

        // Calculate password strength when password field changes
        if (field === 'password') {
          setPasswordStrength(calculatePasswordStrength(value))
          // Re-validate confirm password when password changes
          if (formData.confirmPassword) {
            if (value !== formData.confirmPassword) {
              setFieldErrors(prev => ({
                ...prev,
                confirmPassword: 'Passwords do not match',
              }))
            } else {
              setFieldErrors(prev => ({
                ...prev,
                confirmPassword: '',
              }))
            }
          }
        }
      }

  const handleSelectChange = (field: keyof PatientFormData) => (event: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }))
    // Clear field error when user makes a selection
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: '',
      }))
    }
  }

  const handleCareNeedsChange = (careNeed: string) => {
    setFormData(prev => {
      const currentNeeds = [...prev.careNeeds]
      if (currentNeeds.includes(careNeed)) {
        return {
          ...prev,
          careNeeds: currentNeeds.filter(need => need !== careNeed),
        }
      } else {
        return {
          ...prev,
          careNeeds: [...currentNeeds, careNeed],
        }
      }
    })
    // Clear careNeeds error when user makes a selection
    if (fieldErrors.careNeeds) {
      setFieldErrors(prev => ({
        ...prev,
        careNeeds: '',
      }))
    }
  }

  // Handler for multi-select dropdown - UPDATED to store _id's
  const handleCareNeedsMultiSelectChange = (event: any) => {
    const { value } = event.target

    // Convert selected names to their corresponding _id's
    const selectedIds = value.map((selectedName: string) => {
      const foundNeed = careNeedsOptions.find(
        need => need.name === selectedName
      )
      return foundNeed ? foundNeed._id : selectedName
    })

    setFormData(prev => ({
      ...prev,
      careNeeds: selectedIds,
    }))

    // Clear careNeeds error when user makes a selection
    if (fieldErrors.careNeeds) {
      setFieldErrors(prev => ({
        ...prev,
        careNeeds: '',
      }))
    }
  }

  const handleTherapyHistoryMultiSelectChange = (event: any) => {
    const { value } = event.target

    // Convert selected names to their corresponding _id's
    const selectedIds = value.map((selectedName: string) => {
      const foundItem = therapyHistoryOptions.find(
        item => item.name === selectedName
      )
      return foundItem ? foundItem.name : selectedName
    })

    setFormData(prev => ({
      ...prev,
      therapyHistory: selectedIds,
    }))

    // Clear therapyHistory error when user makes a selection
    if (fieldErrors.therapyHistory) {
      setFieldErrors(prev => ({
        ...prev,
        therapyHistory: '',
      }))
    }
  }

  const handlePhoneChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      phone: value,
    }))

    // Only validate and show error if:
    // 1. Field has been touched (blurred) AND value is invalid
    // 2. OR phone number is complete (10 digits) and invalid
    const digitsOnly = value.replace(/\D/g, '')
    const isComplete = digitsOnly.length === 10

    if (phoneTouched || isComplete) {
      const phoneError = getUSPhoneErrorMessage(value)
      if (phoneError) {
        setFieldErrors(prev => ({
          ...prev,
          phone: phoneError,
        }))
      } else {
        setFieldErrors(prev => ({
          ...prev,
          phone: '',
        }))
      }
    } else {
      // Clear error while typing if field hasn't been touched yet
      if (fieldErrors.phone) {
        setFieldErrors(prev => ({
          ...prev,
          phone: '',
        }))
      }
    }
  }

  const handleConfirmPasswordChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value
    setFormData(prev => ({
      ...prev,
      confirmPassword: value,
    }))

    // Clear confirm password error when user starts typing
    if (fieldErrors.confirmPassword) {
      setFieldErrors(prev => ({
        ...prev,
        confirmPassword: '',
      }))
    }

    // Real-time validation: Check if passwords match
    if (value && formData.password && value !== formData.password) {
      setFieldErrors(prev => ({
        ...prev,
        confirmPassword: 'Passwords do not match',
      }))
    } else if (value && formData.password && value === formData.password) {
      // Clear error if they match
      setFieldErrors(prev => ({
        ...prev,
        confirmPassword: '',
      }))
    }
  }

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token)
    if (token) {
      setRecaptchaError('')
    } else if (isRecaptchaEnabled) {
      setRecaptchaError('Please verify you are not a robot.')
    }
  }

  const handleRecaptchaExpired = () => {
    setRecaptchaToken(null)
    if (isRecaptchaEnabled) {
      setRecaptchaError('Please verify you are not a robot.')
    }
    recaptchaRef.current?.reset()
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setIsSubmitting(true)
    setError('')

    // Validate required fields before proceeding
    if (!formData.gender || !formData.gender.trim()) {
      setFieldErrors(prev => ({
        ...prev,
        gender: 'Gender is required',
      }))
      setIsSubmitting(false)
      return
    }

    if (!formData.dateOfBirth) {
      setFieldErrors(prev => ({
        ...prev,
        dateOfBirth: 'Date of birth is required',
      }))
      setIsSubmitting(false)
      return
    }

    // Validate passwords match before proceeding
    if (formData.password !== formData.confirmPassword) {
      setFieldErrors(prev => ({
        ...prev,
        confirmPassword: 'Passwords do not match',
      }))
      setIsSubmitting(false)
      return
    }

    if (isRecaptchaEnabled && !recaptchaToken) {
      setRecaptchaError('Please verify you are not a robot.')
      setIsSubmitting(false)
      return
    }

    try {
      // Prepare the payload according to the curl structure
      const signupPayload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        gender: formData.gender,
        dob: formData.dateOfBirth
          ? dayjs(formData.dateOfBirth).format('YYYY-MM-DD')
          : undefined,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        city: formData.city,
        country: formData.country,
        state: formData.state,
        postalCode: formData.postalCode,
        latitude: formData.latitude,
        longitude: formData.longitude,
        careNeeds: formData.careNeeds,
        careTaken: formData.therapyHistory,
        sessionFrequency: formData.sessionFrequency,
        careGoal: formData.careGoal,
        recaptchaToken: recaptchaToken || undefined,
      }

      console.log('Signup payload:', signupPayload)

      // Make API call to create patient account
      const response = await api.post('/patient/signup', signupPayload)

      if (response.status === 200 || response.status === 201) {
        setShowSuccessAlert(true)
        if (isRecaptchaEnabled) {
          recaptchaRef.current?.reset()
          setRecaptchaToken(null)
          setRecaptchaError('')
        }
        // Redirect to login after success
        setTimeout(() => {
          navigate('/patient-login')
        }, 2000)
      }
    } catch (error: any) {
      console.error('Error creating patient account:', error)
      const serverMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to create account. Please try again.'
      setError(serverMessage)
      if (isRecaptchaEnabled) {
        recaptchaRef.current?.reset()
        setRecaptchaToken(null)
        if (typeof serverMessage === 'string') {
          const normalizedMessage = serverMessage.toLowerCase()
          if (
            normalizedMessage.includes('captcha') ||
            normalizedMessage.includes('robot')
          ) {
            setRecaptchaError(
              'reCAPTCHA verification failed. Please try again.'
            )
          } else {
            setRecaptchaError('')
          }
        }
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBasicInfoSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    if (!validateBasicInfo()) {
      return
    }

    setShowOnboarding(true)
    setError('')
  }

  const handleLoginClick = () => {
    navigate('/patient-login')
  }

  const handleNext = () => {
    // Only validate step 0 (care needs is mandatory)
    if (currentStep === 0 && !validateOnboardingStep(currentStep)) {
      return
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleSkip = () => {
    // Skip current step and move to next
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    if (currentStep === 0) {
      // If we're on the first step of onboarding, go back to basic info
      setShowOnboarding(false)
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
    // Clear errors when going back
    setFieldErrors({})
    setPhoneTouched(false)
  }

  const handleCompleteOnboarding = (event: React.FormEvent) => {
    event.preventDefault()

    // Only validate step 0 (care needs is mandatory)
    // Other steps are optional, so we can proceed even if they're empty
    if (currentStep === 0 && !validateOnboardingStep(currentStep)) {
      return
    }

    // Submit the complete form
    handleSubmit(event)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <SignupHeader />
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'white',
          display: 'flex',
          flexDirection: 'column',
          marginTop: '0.05rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            minHeight: 0,
            justifyContent: showOnboarding ? 'flex-start' : 'center',
            alignItems: showOnboarding ? 'flex-start' : 'center',
            position: 'relative',
            px: { xs: 1.5, lg: 2 },
            py: showOnboarding ? { xs: 2, md: 3 } : 2,
            mx: 'auto',
            width: '100%',
            maxWidth: '90vw',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              gap: { xs: 0, md: 3, lg: 4 },
              alignItems: 'start',
            }}
          >
            <Slide
              direction="left"
              in
              timeout={1000}
              style={{ transitionDelay: '500ms' }}
            >
              <Box
                sx={{
                  flex: { xs: 0, md: 1.5, mt: 0 },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: showOnboarding ? 'flex-start' : 'center',
                  alignItems: 'flex-start',
                  padding: '0rem',
                  width: '100%',
                }}
              >
                <Grow
                  in
                  timeout={1200}
                  style={{
                    transitionDelay: '700ms',
                    marginTop: 0,
                    background: showOnboarding ? '#F7F9FA' : '',
                    borderRadius: '2rem',
                    padding: showOnboarding ? '2rem' : '0rem',
                  }}
                >
                  <Paper
                    sx={{
                      width: '100%',
                      boxShadow: '0 0px 0px rgba(0,0,0,0.0)',
                      transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 0px 0px rgba(0,0,0,0.0)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                    style={{
                      paddingRight: '0rem',
                      paddingLeft: '0rem',
                    }}
                  >
                    {!showOnboarding ? (
                      <div className="flex justify-start gap-8">
                        <Zoom
                          in
                          timeout={1200}
                          style={{ transitionDelay: '300ms' }}
                        >
                          <Box
                            sx={{
                              flex: { xs: 0, md: 1 },
                              position: 'relative',
                              display: { xs: 'none', md: 'block' },
                              overflow: 'hidden',
                              bgcolor: 'white',
                              borderRadius: 3,
                              mb: 5,
                              mt: 1,
                            }}
                          >
                            <Box
                              component="img"
                              src="/patientCreate1.jpg"
                              alt="Patient create account illustration"
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectPosition: 'center',
                                transition: 'transform 0.3s ease',
                                '&:hover': {
                                  transform: 'scale(1.05)',
                                },
                              }}
                            />
                          </Box>
                        </Zoom>

                        <Box
                          sx={{
                            maxWidth: { xs: '100%', md: '45vw', lg: '40vw' },
                            width: '100%',
                          }}
                        >
                          <Fade
                            in
                            timeout={1000}
                            style={{
                              transitionDelay: '900ms',
                              marginTop: 0,
                            }}
                          >
                            <Typography
                              variant="h4"
                              sx={{
                                fontWeight: 400,
                                color: 'grey.800',
                                mb: 0,
                                fontSize: {
                                  xs: '1.75rem',
                                  sm: '2rem',
                                  md: '2.25rem',
                                },
                                width: '100%',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Create your account
                            </Typography>
                          </Fade>

                          {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                              {error}
                            </Alert>
                          )}

                          <Box
                            component="form"
                            onSubmit={handleBasicInfoSubmit}
                            autoComplete="off"
                          >
                            {/* First Name & Last Name Row */}
                            <Fade
                              in
                              timeout={800}
                              style={{
                                transitionDelay: '1100ms',
                                marginTop: '0.25rem',
                              }}
                            >
                              <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'grey.700',
                                      mb: 0.5,
                                      fontWeight: 500,
                                      fontSize: '0.875rem',
                                    }}
                                  >
                                    First Name *
                                  </Typography>
                                  <TextField
                                    fullWidth
                                    value={formData.firstName}
                                    onChange={handleInputChange('firstName')}
                                    placeholder="Enter First Name"
                                    required
                                    error={!!fieldErrors.firstName}
                                    helperText={fieldErrors.firstName}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        bgcolor: 'white',
                                        borderRadius: 2,
                                        transition: 'all 0.3s ease',
                                        '& fieldset': {
                                          borderColor: 'grey.300',
                                        },
                                        '&:hover fieldset': {
                                          borderColor: 'grey.400',
                                          transform: 'scale(1.02)',
                                        },
                                        '&.Mui-focused fieldset': {
                                          borderColor: '#3BAE78',
                                          borderWidth: 2,
                                        },
                                      },
                                      '& .MuiInputBase-input': {
                                        py: 1.5,
                                        fontSize: '0.875rem',
                                      },
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'grey.700',
                                      mb: 0.5,
                                      fontWeight: 500,
                                      fontSize: '0.875rem',
                                    }}
                                  >
                                    Last Name *
                                  </Typography>
                                  <TextField
                                    fullWidth
                                    placeholder="Enter Last Name"
                                    value={formData.lastName}
                                    onChange={handleInputChange('lastName')}
                                    required
                                    error={!!fieldErrors.lastName}
                                    helperText={fieldErrors.lastName}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        bgcolor: 'white',
                                        borderRadius: 2,
                                        transition: 'all 0.3s ease',
                                        '& fieldset': {
                                          borderColor: 'grey.300',
                                        },
                                        '&:hover fieldset': {
                                          borderColor: 'grey.400',
                                          transform: 'scale(1.02)',
                                        },
                                        '&.Mui-focused fieldset': {
                                          borderColor: '#3BAE78',
                                          borderWidth: 2,
                                        },
                                      },
                                      '& .MuiInputBase-input': {
                                        py: 1.5,
                                        fontSize: '0.875rem',
                                      },
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </Fade>

                            {/* Phone Number Field */}
                            <Fade
                              in
                              timeout={800}
                              style={{ transitionDelay: '1300ms' }}
                            >
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'grey.700',
                                    mb: 0.5,
                                    fontWeight: 500,
                                    fontSize: '0.875rem',
                                  }}
                                >
                                  Phone Number *
                                </Typography>
                                <PhoneInput
                                  fullWidth
                                  value={formData.phone}
                                  onChange={handlePhoneChange}
                                  onBlur={() => {
                                    setPhoneTouched(true)
                                    // Validate on blur
                                    const phoneError = getUSPhoneErrorMessage(
                                      formData.phone
                                    )
                                    if (phoneError) {
                                      setFieldErrors(prev => ({
                                        ...prev,
                                        phone: phoneError,
                                      }))
                                    } else {
                                      setFieldErrors(prev => ({
                                        ...prev,
                                        phone: '',
                                      }))
                                    }
                                  }}
                                  required
                                  error={!!fieldErrors.phone}
                                  helperText={fieldErrors.phone}
                                  showErrorOnBlur={false}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      bgcolor: 'white',
                                      borderRadius: 2,
                                      transition: 'all 0.3s ease',
                                      '& fieldset': { borderColor: 'grey.300' },
                                      '&:hover fieldset': {
                                        borderColor: 'grey.400',
                                        transform: 'scale(1.02)',
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: '#3BAE78',
                                        borderWidth: 2,
                                      },
                                    },
                                    '& .MuiInputBase-input': {
                                      py: 1.5,
                                      fontSize: '0.875rem',
                                    },
                                  }}
                                />
                              </Box>
                            </Fade>

                            {/* Email Field */}
                            <Fade
                              in
                              timeout={800}
                              style={{ transitionDelay: '1500ms' }}
                            >
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'grey.700',
                                    mb: 0.5,
                                    fontWeight: 500,
                                    fontSize: '0.875rem',
                                  }}
                                >
                                  Email *
                                </Typography>
                                <TextField
                                  fullWidth
                                  type="email"
                                  placeholder="Enter Email"
                                  value={formData.email}
                                  onChange={handleInputChange('email')}
                                  inputProps={{ autoComplete: 'off' }}
                                  required
                                  error={!!fieldErrors.email}
                                  helperText={fieldErrors.email}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      bgcolor: 'white',
                                      borderRadius: 2,
                                      transition: 'all 0.3s ease',
                                      '& fieldset': { borderColor: 'grey.300' },
                                      '&:hover fieldset': {
                                        borderColor: 'grey.400',
                                        transform: 'scale(1.02)',
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: '#3BAE78',
                                        borderWidth: 2,
                                      },
                                    },
                                    '& .MuiInputBase-input': {
                                      py: 1.5,
                                      fontSize: '0.875rem',
                                    },
                                  }}
                                />
                              </Box>
                            </Fade>

                            {/* Password & Confirm Password Row */}
                            <Fade
                              in
                              timeout={800}
                              style={{ transitionDelay: '1700ms' }}
                            >
                              <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'grey.700',
                                      mb: 0.5,
                                      fontWeight: 500,
                                      fontSize: '0.875rem',
                                    }}
                                  >
                                    Password *
                                  </Typography>
                                  <TextField
                                    fullWidth
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Enter Password"
                                    value={formData.password}
                                    onChange={handleInputChange('password')}
                                    inputProps={{
                                      autoComplete: 'new-password',
                                    }}
                                    required
                                    error={!!fieldErrors.password}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        bgcolor: 'white',
                                        borderRadius: 2,
                                        transition: 'all 0.3s ease',
                                        '& fieldset': {
                                          borderColor:
                                            formData.password.length > 0
                                              ? passwordStrength.borderColor
                                              : 'grey.300',
                                        },
                                        '&:hover fieldset': {
                                          borderColor:
                                            formData.password.length > 0
                                              ? passwordStrength.borderColor
                                              : 'grey.400',
                                          transform: 'scale(1.02)',
                                        },
                                        '&.Mui-focused fieldset': {
                                          borderColor:
                                            passwordStrength.borderColor,
                                          borderWidth: 2,
                                        },
                                      },
                                      '& .MuiInputBase-input': {
                                        py: 1.5,
                                        fontSize: '0.875rem',
                                      },
                                    }}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <IconButton
                                            onClick={() =>
                                              setShowPassword(!showPassword)
                                            }
                                            edge="end"
                                            sx={{
                                              color: 'grey.400',
                                              transition: 'transform 0.2s ease',
                                              '&:hover': {
                                                transform: 'scale(1.1)',
                                              },
                                            }}
                                          >
                                            {showPassword ? (
                                              <VisibilityOff />
                                            ) : (
                                              <Visibility />
                                            )}
                                          </IconButton>
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                  {formData.password.length > 0 && (
                                    <PasswordStrengthMeter
                                      strength={passwordStrength}
                                    />
                                  )}
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'grey.700',
                                      mb: 0.5,
                                      fontWeight: 500,
                                      fontSize: '0.875rem',
                                    }}
                                  >
                                    Confirm Password *
                                  </Typography>
                                  <TextField
                                    fullWidth
                                    type={
                                      showConfirmPassword ? 'text' : 'password'
                                    }
                                    placeholder="Confirm Password"
                                    value={formData.confirmPassword}
                                    onChange={handleConfirmPasswordChange}
                                    inputProps={{
                                      autoComplete: 'new-password',
                                    }}
                                    required
                                    error={!!fieldErrors.confirmPassword}
                                    helperText={fieldErrors.confirmPassword}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        bgcolor: 'white',
                                        borderRadius: 2,
                                        transition: 'all 0.3s ease',
                                        '& fieldset': {
                                          borderColor:
                                            fieldErrors.confirmPassword
                                              ? '#F44336'
                                              : 'grey.300',
                                        },
                                        '&:hover fieldset': {
                                          borderColor:
                                            fieldErrors.confirmPassword
                                              ? '#F44336'
                                              : 'grey.400',
                                          transform: 'scale(1.02)',
                                        },
                                        '&.Mui-focused fieldset': {
                                          borderColor:
                                            fieldErrors.confirmPassword
                                              ? '#F44336'
                                              : '#3BAE78',
                                          borderWidth: 2,
                                        },
                                        '&.Mui-error fieldset': {
                                          borderColor: '#F44336',
                                        },
                                      },
                                      '& .MuiInputBase-input': {
                                        py: 1.5,
                                        fontSize: '0.875rem',
                                      },
                                    }}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <IconButton
                                            onClick={() =>
                                              setShowConfirmPassword(
                                                !showConfirmPassword
                                              )
                                            }
                                            edge="end"
                                            sx={{
                                              color: 'grey.400',
                                              transition: 'transform 0.2s ease',
                                              '&:hover': {
                                                transform: 'scale(1.1)',
                                              },
                                            }}
                                          >
                                            {showConfirmPassword ? (
                                              <VisibilityOff />
                                            ) : (
                                              <Visibility />
                                            )}
                                          </IconButton>
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </Fade>

                            {/* Gender & Date of Birth Row */}
                            <Fade
                              in
                              timeout={800}
                              style={{ transitionDelay: '1600ms' }}
                            >
                              <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'grey.700',
                                      mb: 0.5,
                                      fontWeight: 500,
                                      fontSize: '0.875rem',
                                    }}
                                  >
                                    Gender *
                                  </Typography>
                                  <FormControl
                                    fullWidth
                                    size="small"
                                    error={!!fieldErrors.gender}
                                  >
                                    <Select
                                      displayEmpty
                                      value={formData.gender}
                                      onChange={handleSelectChange('gender')}
                                      sx={{
                                        bgcolor: 'white',
                                        borderRadius: 2,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                          borderColor: 'grey.300',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline':
                                        {
                                          borderColor: 'grey.400',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                        {
                                          borderColor: '#3BAE78',
                                          borderWidth: 2,
                                        },
                                      }}
                                    >
                                      <MenuItem value="">
                                        <em>Select Gender</em>
                                      </MenuItem>
                                      {genderOptions.map(option => (
                                        <MenuItem key={option} value={option}>
                                          {option}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    {fieldErrors.gender && (
                                      <Typography
                                        variant="caption"
                                        color="error"
                                        sx={{ mt: 0.5, ml: 1.75 }}
                                      >
                                        {fieldErrors.gender}
                                      </Typography>
                                    )}
                                  </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'grey.700',
                                      mb: 0.5,
                                      fontWeight: 500,
                                      fontSize: '0.875rem',
                                    }}
                                  >
                                    Date of Birth *
                                  </Typography>
                                  <DatePicker
                                    maxDate={new Date()}
                                    value={formData.dateOfBirth}
                                    onChange={date => {
                                      // Convert PickerValue (Date | Dayjs) to Date
                                      const dateObj =
                                        date instanceof Date
                                          ? date
                                          : (date as any)?.toDate
                                            ? (date as any).toDate()
                                            : date
                                              ? new Date(date as any)
                                              : null
                                      setFormData(prev => ({
                                        ...prev,
                                        dateOfBirth: dateObj,
                                      }))
                                      if (fieldErrors.dateOfBirth) {
                                        setFieldErrors(prev => ({
                                          ...prev,
                                          dateOfBirth: '',
                                        }))
                                      }
                                    }}
                                    slotProps={{
                                      textField: {
                                        fullWidth: true,
                                        size: 'small',
                                        error: !!fieldErrors.dateOfBirth,
                                        helperText: fieldErrors.dateOfBirth,
                                        placeholder: 'Select Date of Birth',
                                        sx: {
                                          borderRadius: 4,
                                          '& .MuiOutlinedInput-root': {
                                            bgcolor: 'white',
                                            borderRadius: 2,
                                            transition: 'all 0.3s ease',
                                            '& fieldset': {
                                              borderColor: 'grey.300',
                                            },
                                            '&:hover fieldset': {
                                              borderColor: 'grey.400',
                                              transform: 'scale(1.02)',
                                            },
                                            '&.Mui-focused fieldset': {
                                              borderColor: '#3BAE78',
                                              borderWidth: 2,
                                            },
                                          },
                                          '& .MuiInputBase-input': {
                                            fontSize: '0.875rem',
                                          },
                                          '& .MuiInputBase-input:focus': {
                                            borderRadius: '16px',
                                          },
                                          '& .MuiPickersInputBase-root': {
                                            borderRadius: '16px',
                                          },
                                        },
                                      },
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </Fade>

                            {/* Address Line 1 - REPLACE WITH THIS */}
                            <Fade
                              in
                              timeout={800}
                              style={{ transitionDelay: '1900ms' }}
                            >
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'grey.700',
                                    mb: 0.5,
                                    fontWeight: 500,
                                    fontSize: '0.875rem',
                                  }}
                                >
                                  Address Line 1 *
                                </Typography>

                                {/* Add debugging */}
                                {/* <Box sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Debug: {formData.addressLine1 || 'Empty'}
                                  </Typography>
                                </Box>
                                 */}
                                <AddressAutocomplete
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      bgcolor: 'white',
                                      borderRadius: 2,
                                      transition: 'all 0.3s ease',
                                      '& fieldset': { borderColor: 'grey.300' },
                                      '&:hover fieldset': {
                                        borderColor: 'grey.400',
                                        transform: 'scale(1.02)',
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: '#3BAE78',
                                        borderWidth: 2,
                                      },
                                    },
                                    '& .MuiInputBase-input': {
                                      py: 1.5,
                                      fontSize: '0.875rem',
                                    },
                                  }}
                                  value={formData.addressLine1}
                                  onChange={(address, components) => {
                                    setFormData(prev => ({
                                      ...prev,
                                      addressLine1: address,
                                      // Only update city, state, zip if components are provided and have values
                                      // Don't clear them when address is cleared (components will have empty strings)
                                      ...(components && {
                                        ...(components.country && {
                                          country: components.country,
                                        }),
                                        ...(components.city && {
                                          city: components.city,
                                        }),
                                        ...(components.state && {
                                          state: components.state,
                                        }),
                                        ...((components.zipCode ||
                                          components.postalCode) && {
                                          postalCode:
                                            components.zipCode ||
                                            components.postalCode,
                                        }),
                                        ...(components.addressLine2 && {
                                          addressLine2: components.addressLine2,
                                        }),
                                        // Capture latitude and longitude
                                        ...(components.latitude !==
                                          undefined && {
                                          latitude: components.latitude,
                                        }),
                                        ...(components.longitude !==
                                          undefined && {
                                          longitude: components.longitude,
                                        }),
                                      }),
                                    }))
                                    // Clear field error when user starts typing
                                    if (fieldErrors.addressLine1) {
                                      setFieldErrors(prev => ({
                                        ...prev,
                                        addressLine1: '',
                                      }))
                                    }
                                  }}
                                  placeholder="Start typing your address..."
                                  error={!!fieldErrors.addressLine1}
                                  helperText={fieldErrors.addressLine1}
                                  addressType="address"
                                />
                              </Box>
                            </Fade>

                            {/* Address Line 2 */}
                            <Fade
                              in
                              timeout={800}
                              style={{ transitionDelay: '2100ms' }}
                            >
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'grey.700',
                                    mb: 0.5,
                                    fontWeight: 500,
                                    fontSize: '0.875rem',
                                  }}
                                >
                                  Address Line 2
                                </Typography>
                                <TextField
                                  fullWidth
                                  placeholder="Enter Address Line 2 (Optional)"
                                  value={formData.addressLine2}
                                  onChange={handleInputChange('addressLine2')}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      bgcolor: 'white',
                                      borderRadius: 2,
                                      transition: 'all 0.3s ease',
                                      '& fieldset': { borderColor: 'grey.300' },
                                      '&:hover fieldset': {
                                        borderColor: 'grey.400',
                                        transform: 'scale(1.02)',
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: '#3BAE78',
                                        borderWidth: 2,
                                      },
                                    },
                                    '& .MuiInputBase-input': {
                                      py: 1.5,
                                      fontSize: '0.875rem',
                                    },
                                  }}
                                />
                              </Box>
                            </Fade>

                            {/* City & Postal Code Row */}
                            <Fade
                              in
                              timeout={800}
                              style={{ transitionDelay: '2300ms' }}
                            >
                              <Grid container spacing={4} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'grey.700',
                                      mb: 0.5,
                                      fontWeight: 500,
                                      fontSize: '0.875rem',
                                    }}
                                  >
                                    City *
                                  </Typography>
                                  <TextField
                                    fullWidth
                                    placeholder="Enter City"
                                    value={formData.city}
                                    onChange={handleInputChange('city')}
                                    required
                                    error={!!fieldErrors.city}
                                    helperText={fieldErrors.city}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        bgcolor: 'white',
                                        borderRadius: 2,
                                        transition: 'all 0.3s ease',
                                        '& fieldset': {
                                          borderColor: 'grey.300',
                                        },
                                        '&:hover fieldset': {
                                          borderColor: 'grey.400',
                                        },
                                        '&.Mui-focused fieldset': {
                                          borderColor: '#3BAE78',
                                          borderWidth: 2,
                                        },
                                      },
                                      '& .MuiInputBase-input': {
                                        py: 1.5,
                                        fontSize: '0.875rem',
                                      },
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'grey.700',
                                      mb: 0.5,
                                      fontWeight: 500,
                                      fontSize: '0.875rem',
                                    }}
                                  >
                                    Postal Code *
                                  </Typography>
                                  <TextField
                                    fullWidth
                                    placeholder="Enter Postal Code"
                                    value={formData.postalCode}
                                    onChange={handleInputChange('postalCode')}
                                    required
                                    error={!!fieldErrors.postalCode}
                                    helperText={fieldErrors.postalCode}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        bgcolor: 'white',
                                        borderRadius: 2,
                                        transition: 'all 0.3s ease',
                                        '& fieldset': {
                                          borderColor: 'grey.300',
                                        },
                                        '&:hover fieldset': {
                                          borderColor: 'grey.400',
                                        },
                                        '&.Mui-focused fieldset': {
                                          borderColor: '#3BAE78',
                                          borderWidth: 2,
                                        },
                                      },
                                      '& .MuiInputBase-input': {
                                        py: 1.5,
                                        fontSize: '0.875rem',
                                      },
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </Fade>

                            {/* Country → State cascading dropdowns */}
                            <Fade
                              in
                              timeout={800}
                              style={{ transitionDelay: '2500ms' }}
                            >
                              <Box sx={{ mb: 4 }}>
                                <LocationSelector
                                  country={formData.country}
                                  state={formData.state}
                                  onCountryChange={name =>
                                    setFormData(prev => ({
                                      ...prev,
                                      country: name,
                                      state: '',
                                    }))
                                  }
                                  onStateChange={name =>
                                    setFormData(prev => ({
                                      ...prev,
                                      state: name,
                                    }))
                                  }
                                  countryError={fieldErrors.country}
                                  stateError={fieldErrors.state}
                                  accentColor="#3BAE78"
                                  required
                                />
                              </Box>
                            </Fade>

                            {/* Continue Button */}
                            <Fade
                              in
                              timeout={800}
                              style={{ transitionDelay: '2700ms' }}
                            >
                              <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{
                                  bgcolor: '#3BAE78',
                                  py: 1.5,
                                  fontSize: '1rem',
                                  fontWeight: 600,
                                  borderRadius: 2,
                                  mb: 2,
                                  textTransform: 'none',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    bgcolor: '#3BAE78',
                                    transform: 'translateY(-2px)',
                                    boxShadow:
                                      '0 4px 12px rgba(46, 125, 175, 0.3)',
                                  },
                                }}
                              >
                                Continue
                              </Button>
                            </Fade>
                          </Box>

                          {/* Login Link */}
                          <Fade
                            in
                            timeout={800}
                            style={{ transitionDelay: '2900ms' }}
                          >
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: 'grey.600', fontSize: '0.875rem' }}
                              >
                                Already have an account?{' '}
                                <Link
                                  component="button"
                                  onClick={handleLoginClick}
                                  sx={{
                                    color: '#3BAE78',
                                    textDecoration: 'none',
                                    fontWeight: 600,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                      textDecoration: 'underline',
                                      transform: 'translateY(-1px)',
                                    },
                                  }}
                                >
                                  Login
                                </Link>
                              </Typography>
                            </Box>
                          </Fade>
                        </Box>
                      </div>
                    ) : (
                      // Onboarding Form
                      <div className="flex flex-col justify-start gap-8">
                        <Fade in timeout={800}>
                          <Box sx={{ textAlign: 'center', mb: 4 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 300,
                                color: 'grey.700',
                                mb: 0.5,
                                fontSize: {
                                  xs: '1.75rem',
                                  sm: '2.25rem',
                                  md: '2.5rem',
                                },
                              }}
                            >
                              Help us match you to the right provider
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{
                                color: 'grey.600',
                                fontWeight: 400,
                                fontSize: { xs: '1rem', sm: '1.125rem' },
                              }}
                            >
                              <small className="mt-4 mb-0 text-gray-500">
                                A strong connection with your provider is key.
                              </small>{' '}
                              <br />
                              <small className="mt-2 mb-2 text-gray-500">
                                {' '}
                                Answer a few short questions to be matched with
                                a licensed professional who fits your needs.
                              </small>
                            </Typography>
                          </Box>
                        </Fade>

                        <Fade
                          in
                          timeout={800}
                          style={{ transitionDelay: '200ms' }}
                        >
                          <Box sx={{ mb: 6 }}>
                            <Stepper
                              connector={null}
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 2,
                              }}
                            >
                              {steps.map((_, index) => (
                                <Step
                                  key={index}
                                  sx={{ p: 0, m: 0, minWidth: 'auto' }}
                                >
                                  <StepLabel
                                    sx={{ m: 0, p: 0 }}
                                    icon={
                                      <Box
                                        sx={{
                                          width: 28,
                                          height: 8,
                                          borderRadius: 999,
                                          backgroundColor:
                                            index <= currentStep
                                              ? '#3BAE78'
                                              : '#E0E0E0',
                                          transition: 'all 0.3s ease-in-out',
                                        }}
                                      />
                                    }
                                  />
                                </Step>
                              ))}
                            </Stepper>
                          </Box>
                        </Fade>

                        {error && (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                          </Alert>
                        )}

                        {/* Step 0: Care Needs - Dynamic Dropdown */}
                        {currentStep === 0 && (
                          <Box sx={{ textAlign: 'center', mb: 4 }}>
                            <Fade
                              in
                              timeout={800}
                              style={{ transitionDelay: '400ms' }}
                            >
                              <Box
                                sx={{
                                  textAlign: 'center',
                                  mb: 1,
                                  px: { xs: 1, sm: 1.5, lg: 2 },
                                  mx: 'auto',
                                  width: '100%',
                                  maxWidth: {
                                    xs: '100%',
                                    sm: '85vw',
                                    lg: '80vw',
                                  },
                                }}
                              >
                                <Typography
                                  variant="h5"
                                  sx={{
                                    color: 'grey.700',
                                    mb: 1.5,
                                    fontWeight: 400,
                                    fontSize: '1.2rem',
                                  }}
                                >
                                  What kind of care or services do you need?
                                </Typography>
                                <FormControl fullWidth>
                                  <Box>
                                    <Select
                                      fullWidth
                                      multiple
                                      value={formData.careNeeds.map(id => {
                                        const foundNeed = careNeedsOptions.find(
                                          need => need._id === id
                                        )
                                        return foundNeed ? foundNeed.name : id
                                      })}
                                      onChange={
                                        handleCareNeedsMultiSelectChange
                                      }
                                      displayEmpty
                                      disabled={loadingCareNeeds}
                                      sx={{
                                        borderRadius: 2,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                          borderColor: 'grey.300',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline':
                                        {
                                          borderColor: 'grey.400',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                        {
                                          borderColor: '#3BAE78',
                                          borderWidth: 2,
                                        },
                                      }}
                                      renderValue={selected => {
                                        if (
                                          (selected as string[]).length === 0
                                        ) {
                                          return (
                                            <em style={{ color: 'grey.400' }}>
                                              {loadingCareNeeds
                                                ? 'Loading care needs...'
                                                : 'Select care needs'}
                                            </em>
                                          )
                                        }
                                        return (
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              flexWrap: 'wrap',
                                              gap: 0.5,
                                            }}
                                          >
                                            {(selected as string[]).map(
                                              value => (
                                                <Chip
                                                  key={value}
                                                  label={value}
                                                  variant="outlined"
                                                  sx={{
                                                    borderRadius: '8px',
                                                    borderColor: '#90caf9',
                                                    backgroundColor: '#e3f2fd',
                                                    color: '#1976d2',
                                                    fontWeight: 500,
                                                    justifyContent: 'center', // ✅ Centers horizontally
                                                    alignItems: 'center', // ✅ Centers vertically
                                                  }}
                                                />
                                              )
                                            )}
                                          </Box>
                                        )
                                      }}
                                    >
                                      {careNeedsOptions.map(need => (
                                        <MenuItem
                                          key={need._id}
                                          value={need.name}
                                        >
                                          {need.name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </Box>
                                </FormControl>
                                {fieldErrors.careNeeds && (
                                  <Typography
                                    color="error"
                                    variant="caption"
                                    sx={{ mt: 1, display: 'block' }}
                                  >
                                    {fieldErrors.careNeeds}
                                  </Typography>
                                )}
                              </Box>
                            </Fade>
                          </Box>
                        )}

                        {/* Step 1: Therapy History */}
                        {currentStep === 1 && (
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography
                              variant="h5"
                              sx={{
                                color: 'grey.700',
                                mb: 1.5,
                                fontWeight: 400,
                                fontSize: '1.2rem',
                              }}
                              mb={2}
                            >
                              Have you ever been in the following care before?
                              (Choose all that applied)
                            </Typography>
                            <FormControl fullWidth>
                              <Box>
                                <Select
                                  fullWidth
                                  multiple
                                  value={formData.therapyHistory.map(value => {
                                    const foundNeed =
                                      therapyHistoryOptions.find(
                                        need => need.name === value
                                      )
                                    return foundNeed ? foundNeed.name : value
                                  })}
                                  onChange={
                                    handleTherapyHistoryMultiSelectChange
                                  }
                                  displayEmpty
                                  disabled={loadingTherapyHistory}
                                  sx={{
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: 'grey.300',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline':
                                    {
                                      borderColor: 'grey.400',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                    {
                                      borderColor: '#3BAE78',
                                      borderWidth: 2,
                                    },
                                  }}
                                  renderValue={selected => {
                                    if ((selected as string[]).length === 0) {
                                      return (
                                        <em style={{ color: 'grey.400' }}>
                                          {loadingTherapyHistory
                                            ? 'Loading therapy history...'
                                            : 'Select Care'}
                                        </em>
                                      )
                                    }
                                    return (
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          flexWrap: 'wrap',
                                          gap: 0.5,
                                        }}
                                      >
                                        {(selected as string[]).map(value => (
                                          <Chip
                                            key={value}
                                            label={value}
                                            variant="outlined"
                                            sx={{
                                              borderRadius: '8px',
                                              borderColor: '#90caf9',
                                              backgroundColor: '#e3f2fd',
                                              color: '#1976d2',
                                              fontWeight: 500,
                                              justifyContent: 'center', // ✅ Centers horizontally
                                              alignItems: 'center', // ✅ Centers vertically
                                            }}
                                          />
                                        ))}
                                      </Box>
                                    )
                                  }}
                                >
                                  {therapyHistoryOptions.map(item => (
                                    <MenuItem key={item.id} value={item.name}>
                                      {item.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </Box>
                            </FormControl>
                          </Box>
                        )}

                        {/* Step 2: Session Frequency */}
                        {currentStep === 2 && (
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography
                              variant="h5"
                              sx={{
                                color: 'grey.700',
                                mb: 1.5,
                                fontWeight: 400,
                                fontSize: '1.2rem',
                              }}
                              mb={2}
                            >
                              How often would you like to have sessions?
                              (Weekly/Bi Weekly/As Needed)
                            </Typography>

                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 3,
                                flexWrap: 'wrap',
                              }}
                            >
                              {sessionFrequencyOptions.map(freq => (
                                <Button
                                  key={freq}
                                  variant={
                                    formData.sessionFrequency === freq
                                      ? 'contained'
                                      : 'outlined'
                                  }
                                  sx={{
                                    bgcolor:
                                      formData.sessionFrequency === freq
                                        ? '#3BAE78'
                                        : 'transparent',
                                    color:
                                      formData.sessionFrequency === freq
                                        ? 'white'
                                        : '#3BAE78',
                                    borderColor: '#3BAE78',
                                    width: 120,
                                    '&:hover': {
                                      bgcolor:
                                        formData.sessionFrequency === freq
                                          ? '#2d8c5f'
                                          : '#f5f5f5',
                                    },
                                  }}
                                  onClick={() => {
                                    const isCurrentlySelected =
                                      formData.sessionFrequency === freq
                                    // Toggle: if already selected, deselect it (don't advance)
                                    if (isCurrentlySelected) {
                                      setFormData(prev => ({
                                        ...prev,
                                        sessionFrequency: '',
                                      }))
                                    } else {
                                      // Select it and auto-advance
                                      setFormData(prev => ({
                                        ...prev,
                                        sessionFrequency: freq,
                                      }))
                                      setTimeout(() => {
                                        setCurrentStep(prevStep => {
                                          if (
                                            prevStep === 2 &&
                                            prevStep < steps.length - 1
                                          ) {
                                            return prevStep + 1
                                          }
                                          return prevStep
                                        })
                                      }, 300)
                                    }
                                    if (fieldErrors.sessionFrequency) {
                                      setFieldErrors(prev => ({
                                        ...prev,
                                        sessionFrequency: '',
                                      }))
                                    }
                                  }}
                                >
                                  {freq.charAt(0).toUpperCase() +
                                    freq.slice(1).replace('-', ' ')}
                                </Button>
                              ))}
                            </Box>
                            {fieldErrors.sessionFrequency && (
                              <Typography
                                color="error"
                                variant="caption"
                                sx={{ mt: 2, display: 'block' }}
                              >
                                {fieldErrors.sessionFrequency}
                              </Typography>
                            )}
                          </Box>
                        )}

                        {/* Step 3: Care Goals */}
                        {currentStep === 3 && (
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography
                              variant="h5"
                              sx={{
                                color: 'grey.700',
                                mb: 1,
                                fontWeight: 400,
                                fontSize: '1.2rem',
                              }}
                            >
                              What brings you here? (write comment)
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'grey.600',
                                mb: 2,
                                fontSize: '0.875rem',
                                fontStyle: 'italic',
                              }}
                            >
                              (Optional - You can add this later in your
                              profile)
                            </Typography>
                            <Fade
                              in
                              timeout={800}
                              style={{ transitionDelay: '600ms' }}
                            >
                              <Box
                                sx={{
                                  mb: 0,
                                  textAlign: 'start',
                                  position: 'relative',
                                  px: { xs: 1.5, lg: 2 },
                                  py: 2,
                                  mx: 'auto',
                                  width: '100%',
                                  maxWidth: '90vw',
                                }}
                              >
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={8}
                                  value={formData.careGoal}
                                  onChange={handleInputChange('careGoal')}
                                  placeholder="Looking for a licensed provider to provide weekly in-home sessions for physical and occupational therapy, monthly nursing sessions, and daily caregiving service."
                                  error={!!fieldErrors.careGoal}
                                  helperText={fieldErrors.careGoal}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      bgcolor: 'white',
                                      borderRadius: 2,
                                      transition: 'all 0.3s ease',
                                      '& fieldset': {
                                        borderColor: 'grey.300',
                                      },
                                      '&:hover fieldset': {
                                        borderColor: 'grey.400',
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: '#3BAE78',
                                        borderWidth: 2,
                                      },
                                    },
                                    '& .MuiInputBase-input': {
                                      fontSize: '0.875rem',
                                      lineHeight: 1.6,
                                    },
                                  }}
                                />
                                <Typography
                                  variant="subtitle2"
                                  color="gray"
                                  m={2}
                                  mt={1}
                                  mb={0}
                                >
                                  <small>
                                    Sharing your goals helps your provider
                                    personalize your experience. You can always
                                    update this later.{' '}
                                  </small>
                                </Typography>
                              </Box>
                            </Fade>
                          </Box>
                        )}

                        {/* Navigation Buttons */}
                        <Fade
                          in
                          timeout={800}
                          style={{ transitionDelay: '600ms' }}
                          className="mx-20"
                        >
                          <Box
                            sx={{
                              mt: 6,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 3,
                            }}
                          >
                            {isRecaptchaEnabled &&
                              currentStep === steps.length - 1 && (
                                <Box
                                  sx={{
                                    alignSelf: 'center',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <ReCAPTCHA
                                    ref={recaptchaRef}
                                    sitekey={recaptchaSiteKey ?? ''}
                                    onChange={handleRecaptchaChange}
                                    onExpired={handleRecaptchaExpired}
                                  />
                                  {recaptchaError && (
                                    <Typography
                                      variant="caption"
                                      color="error"
                                      sx={{ mt: 0.5 }}
                                    >
                                      {recaptchaError}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Button
                                onClick={handleBack}
                                sx={{
                                  color: 'grey.600',
                                  '&:hover': {
                                    bgcolor: 'grey.50',
                                  },
                                }}
                              >
                                Back
                              </Button>
                              {currentStep === steps.length - 1 ? (
                                <Button
                                  variant="contained"
                                  onClick={handleCompleteOnboarding}
                                  disabled={
                                    isSubmitting ||
                                    (isRecaptchaEnabled && !recaptchaToken)
                                  }
                                  sx={{
                                    bgcolor: '#3BAE78',
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    '&:hover': {
                                      bgcolor: '#2d8c5f',
                                    },
                                    '&.Mui-disabled': {
                                      bgcolor: 'grey.300',
                                      color: 'grey.500',
                                    },
                                  }}
                                >
                                  {isSubmitting ? (
                                    <CircularProgress size={24} />
                                  ) : (
                                    'Create Account'
                                  )}
                                </Button>
                              ) : currentStep <= 1 ? (
                                <Button
                                  variant="contained"
                                  onClick={handleNext}
                                  disabled={formData.careNeeds.length === 0}
                                  sx={{
                                    bgcolor: '#3BAE78',
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    '&:hover': {
                                      bgcolor: '#2d8c5f',
                                    },
                                    '&.Mui-disabled': {
                                      bgcolor: 'grey.300',
                                      color: 'grey.500',
                                    },
                                  }}
                                >
                                  Next
                                </Button>
                              ) : (
                                <Button
                                  variant="outlined"
                                  onClick={handleSkip}
                                  sx={{
                                    color: 'grey.600',
                                    borderColor: 'grey.300',
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    '&:hover': {
                                      bgcolor: 'grey.50',
                                      borderColor: 'grey.400',
                                    },
                                  }}
                                >
                                  Skip
                                </Button>
                              )}
                            </Box>
                          </Box>
                        </Fade>
                      </div>
                    )}
                  </Paper>
                </Grow>
              </Box>
            </Slide>
          </Box>
        </Box>

        {/* Success Alert */}
        <Snackbar
          open={showSuccessAlert}
          autoHideDuration={2000}
          onClose={() => setShowSuccessAlert(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setShowSuccessAlert(false)}
            severity="success"
            sx={{ width: '100%' }}
          >
            Account created successfully! Redirecting to login...
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  )
}

export default PatientCreateAccountPage
